import { useState, useEffect, useMemo, useCallback, Suspense, lazy } from "react";
import Navbar from "../components/sections/Navbar";
import Breadcrumb from "../components/ui/Breadcrumb";
import PageHeader from "../components/sections/PageHeader";
import Button from "../components/ui/Button";
import FilterBar from "../components/sections/FilterBar";
import Tabs from "../components/sections/Tabs";
import { useAuth } from "../context/AuthContext";
import { PdfExportProvider, usePdfExport } from "../context/PdfExportContext";

const AlgemeenTab = lazy(() => import("./tabs/AlgemeenTab"));
const ContextErvaringTab = lazy(() => import("./tabs/ContextErvaringTab"));
const ErvaringTab = lazy(() => import("./tabs/ErvaringTab"));
const JeugdhulpTab = lazy(() => import("./tabs/JeugdhulpTab"));

const PERIOD_LABELS = {
  all: "Alle tijd",
  "12m": "Laatste 12 maanden",
  "30d": "Laatste 30 dagen",
  "7d": "Laatste 7 dagen",
  "24h": "Laatste 24 uur",
};

const FILTER_FIELDS = [
  { key: "aanbieder", label: "Aanbieder", optionsKey: "aanbieders" },
  { key: "gemeente", label: "Gemeente", optionsKey: "gemeentes" },
  { key: "doelgroep", label: "Doelgroep", optionsKey: "doelgroepen" },
  { key: "wanneer", label: "Wanneer ervaring", optionsKey: "wanneer" },
];

const DEFAULT_FILTERS = FILTER_FIELDS.reduce((acc, field) => {
  const initialValue = field.key === "wanneer" ? [] : "";
  return { ...acc, [field.key]: initialValue };
}, {});

const DEFAULT_FILTER_OPTIONS = FILTER_FIELDS.reduce(
  (acc, field) => ({ ...acc, [field.optionsKey]: [] }),
  {}
);

const FILTER_BAR_FIELDS = [...FILTER_FIELDS.map((field) => field.key), "periode", "weergave"];

const DEFAULT_DASHBOARD_DATA = {
  algemeenMetrics: null,
  contextErvaringMetrics: null,
  ceoTextAnswers: null,
  ervaringMetrics: null,
  jeugdhulpMetrics: null,
  jeugdhulpDuurCounts: null,
  jeugdhulpSnelheidCounts: null,
};

const ACTIVE_TAB_STORAGE_KEY = "dashboard_active_tab";

const getStoredActiveTab = () => {
  if (typeof window === "undefined") {
    return "";
  }
  return window.localStorage.getItem(ACTIVE_TAB_STORAGE_KEY) || "";
};

const CSV_DATE_SUFFIX = () => new Date().toISOString().split("T")[0];

const sanitizeFilename = (value) => {
  const cleaned = String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return cleaned || "export";
};

const buildCsvString = (lines = []) =>
  lines
    .map((line) =>
      line
        .map((value) => {
          const safe = `${value ?? ""}`.replace(/"/g, '""');
          return `"${safe}"`;
        })
        .join(",")
    )
    .join("\n");

const downloadCsv = ({ title, header = [], rows = [], suffix = "" }) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return;
  }
  const lines = header.length ? [header, ...rows] : rows;
  const csv = buildCsvString(lines);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const filenameBase = `${sanitizeFilename(title)}${suffix ? `_${suffix}` : ""}`;
  link.href = url;
  link.setAttribute("download", `${filenameBase}_${CSV_DATE_SUFFIX()}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const buildStackedRows = (categories = [], series = []) => {
  const header = ["Categorie", ...series.map((item) => item?.name || "Serie")];
  const rows = categories.map((category, idx) => [
    category,
    ...series.map((item) => Number(item?.data?.[idx]) || 0),
  ]);
  return { header, rows };
};

const normalizeRadialData = (items = []) => {
  const labels = items.map((item) => item.label);
  const absoluteValues = items.map((item) => Number(item?.count) || 0);
  const total = absoluteValues.reduce((acc, value) => acc + Math.max(value, 0), 0);

  const percentageSeries =
    total > 0
      ? absoluteValues.map((value) => Number(((value / total) * 100).toFixed(2)))
      : absoluteValues.map(() => 0);

  return {
    labels,
    absoluteValues,
    percentageSeries,
  };
};

const GEVOELENS_ORDER = [
  "Dankbaar",
  "Gefrustreerd",
  "Hoopvol",
  "Bezorgd",
  "Blij",
  "Boos",
  "Opgelucht",
  "Teleurgesteld",
  "Anders, namelijk..",
];

const normalizeGevoelensLabel = (label) =>
  String(label || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/[.,/#!$%^&*;:{}=\-_`~()"'?<>[\]|\\]/g, "")
    .toLowerCase();

const buildGevoelensExport = (gevoelensCounts = []) => {
  const canonicalGevoelens = new Map(
    GEVOELENS_ORDER.map((label) => [normalizeGevoelensLabel(label), label])
  );
  const gevoelensLookup = new Map();
  const extraGevoelens = [];
  const seenExtra = new Set();

  gevoelensCounts.forEach((g) => {
    const rawLabel = String(g.label || "").trim();
    if (!rawLabel) {
      return;
    }
    const normalized = normalizeGevoelensLabel(rawLabel);
    const canonicalLabel = canonicalGevoelens.get(normalized) || rawLabel;
    const key = normalizeGevoelensLabel(canonicalLabel);
    const prev = gevoelensLookup.get(key) || {
      label: canonicalLabel,
      count: 0,
      count_rel: 0,
    };
    const next = {
      label: prev.label,
      count: (Number(prev.count) || 0) + (Number(g.count) || 0),
      count_rel: (Number(prev.count_rel) || 0) + (Number(g.count_rel) || 0),
    };
    gevoelensLookup.set(key, next);

    if (!canonicalGevoelens.has(normalized) && !seenExtra.has(key)) {
      extraGevoelens.push(canonicalLabel);
      seenExtra.add(key);
    }
  });

  const labels = [...GEVOELENS_ORDER, ...extraGevoelens];
  const rows = labels.map((label) => {
    const entry = gevoelensLookup.get(normalizeGevoelensLabel(label));
    return [label, Number(entry?.count) || 0, Number(entry?.count_rel) || 0];
  });

  return {
    header: ["Gevoel", "Aantal", "%"],
    rows,
  };
};

const buildExportDefinitions = ({ tabKey, data, mode }) => {
  const suffix = mode === "rel" ? "relatief" : "absoluut";
  if (tabKey === "algemeen") {
    const metrics = data?.algemeenMetrics || {};
    const perYear = Array.isArray(metrics.per_year) ? metrics.per_year : [];
    const perYearRows = perYear.map((item) => [
      item.year ?? "",
      Number(item?.count) || 0,
      Number(item?.count_rel) || 0,
    ]);
    const counts = metrics.counts || {};
    const donutRows = [
      ["Jeugdigen", Number(counts.Jongere) || 0, Number(counts.Jongere_rel) || 0],
      ["Ouders/Verzorgers", Number(counts.Ouder_verzorger) || 0, Number(counts.Ouder_verzorger_rel) || 0],
    ];
    return [
      {
        title: "Vragenlijsten per jaar",
        header: ["Jaar", "Aantal", "%"],
        rows: perYearRows,
        suffix,
      },
      {
        title: "Verdeling jeugd vs. ouders/verzorgers",
        header: ["Categorie", "Aantal", "%"],
        rows: donutRows,
        suffix,
      },
    ];
  }

  if (tabKey === "context-ervaring") {
    const source = data?.contextErvaringMetrics || {};
    const ervaringCounts = Array.isArray(source.ervaring_counts) ? source.ervaring_counts : [];
    const gevoelensCounts = Array.isArray(source.gevoelens_counts) ? source.gevoelens_counts : [];
    const agePyramid = source?.leeftijd_groepen?.series
      ? source.leeftijd_groepen
      : { labels: [], series: [], rel: [] };
    const ervaringOver = source?.ervaring_over?.series
      ? source.ervaring_over
      : { categories: [], series: [], series_rel: [] };
    const teamCategoryCounts = source?.category_teams_counts?.series
      ? source.category_teams_counts
      : { categories: [], series: [], series_rel: [] };

    const ervaringRows = ervaringCounts.map((item) => [
      item.label ?? "",
      Number(item?.count) || 0,
      Number(item?.count_rel) || 0,
    ]);

    const gevoelensExport = buildGevoelensExport(gevoelensCounts);
    const ageLabels = agePyramid?.labels ?? [];
    const ageDataRaw =
      agePyramid?.series?.[1]?.data ?? agePyramid?.series?.[0]?.data ?? [];
    const ageAbs = Array.isArray(ageDataRaw) ? ageDataRaw.map((v) => Math.abs(Number(v) || 0)) : [];
    const ageRel = Array.isArray(agePyramid?.rel) ? agePyramid.rel : [];
    const ageRows = ageLabels.map((label, idx) => [
      label,
      Number(ageAbs[idx]) || 0,
      Number(ageRel[idx]) || 0,
    ]);

    const ervaringOverSeries = mode === "rel" ? ervaringOver.series_rel : ervaringOver.series;
    const ervaringOverTable = buildStackedRows(ervaringOver.categories, ervaringOverSeries);
    const teamSeries = mode === "rel" ? teamCategoryCounts.series_rel : teamCategoryCounts.series;
    const teamTable = buildStackedRows(teamCategoryCounts.categories, teamSeries);

    return [
      {
        title: "Ervaring Likert Scores",
        header: ["Score", "Aantal", "%"],
        rows: ervaringRows,
        suffix,
      },
      {
        title: "Gevoelens bij je verhaal",
        header: gevoelensExport.header,
        rows: gevoelensExport.rows,
        suffix,
      },
      {
        title: "Leeftijdsgroepen (Piramide)",
        header: ["Leeftijdsgroep", "Aantal", "%"],
        rows: ageRows,
        suffix,
      },
      {
        title: "Ervaring over (per onderwerp)",
        header: ervaringOverTable.header,
        rows: ervaringOverTable.rows,
        suffix,
      },
      {
        title: "Teams per categorie (stacks)",
        header: teamTable.header,
        rows: teamTable.rows,
        suffix,
      },
    ];
  }

  if (tabKey === "ervaring") {
    const metrics = data?.ervaringMetrics || {};
    const hulpProces = Array.isArray(metrics.hulp_proces) ? metrics.hulp_proces : [];
    const hulpVooralOm = Array.isArray(metrics.hulp_vooral_om) ? metrics.hulp_vooral_om : [];
    const effectHulp = Array.isArray(metrics.effect_hulp) ? metrics.effect_hulp : [];
    const mijBegrijpen = Array.isArray(metrics.mij_begrijpen) ? metrics.mij_begrijpen : [];
    const timeline = Array.isArray(metrics.ervaring_timeline) ? metrics.ervaring_timeline : [];

    const hulpProcesData = normalizeRadialData(hulpProces);
    const hulpVooralData = normalizeRadialData(hulpVooralOm);
    const effectData = normalizeRadialData(effectHulp);

    const radialRows = (labels, absolute, percent) =>
      labels.map((label, idx) => [label, Number(absolute[idx]) || 0, Number(percent[idx]) || 0]);

    const mijRows = mijBegrijpen.map((item) => [
      item.label ?? "",
      Number.isFinite(item?.average_score) ? Number(item.average_score.toFixed(2)) : 0,
      Number(item?.count) || 0,
    ]);

    const timelineRows = timeline
      .filter(
        (point) => Number(point?.count) > 0 && Number.isFinite(point?.average_score)
      )
      .map((point) => [
        point.label ?? "",
        Number(point.average_score.toFixed(2)),
        Number(point?.count) || 0,
      ]);

    return [
      {
        title: "Hulp proces",
        header: ["Label", "Aantal", "%"],
        rows: radialRows(
          hulpProcesData.labels,
          hulpProcesData.absoluteValues,
          hulpProcesData.percentageSeries
        ),
      },
      {
        title: "Gekregen hulp ging vooral om",
        header: ["Label", "Aantal", "%"],
        rows: radialRows(
          hulpVooralData.labels,
          hulpVooralData.absoluteValues,
          hulpVooralData.percentageSeries
        ),
      },
      {
        title: "Effect gekregen hulp",
        header: ["Label", "Aantal", "%"],
        rows: radialRows(
          effectData.labels,
          effectData.absoluteValues,
          effectData.percentageSeries
        ),
      },
      {
        title: "Ervaringsvragen (tijdlijn)",
        header: ["Wanneer ervaring", "Gemiddelde score", "Aantal antwoorden"],
        rows: timelineRows,
      },
      {
        title: "Bejegening",
        header: ["Vraag", "Gemiddelde score", "Aantal antwoorden"],
        rows: mijRows,
      },
    ];
  }

  if (tabKey === "jeugdhulp") {
    const metrics = data?.jeugdhulpMetrics || {};
    const duurCounts = Array.isArray(data?.jeugdhulpDuurCounts) ? data.jeugdhulpDuurCounts : [];
    const snelheidCounts = Array.isArray(data?.jeugdhulpSnelheidCounts)
      ? data.jeugdhulpSnelheidCounts
      : [];
    const heatmap =
      metrics.duur_vs_snelheid && Array.isArray(metrics.duur_vs_snelheid.series)
        ? {
            ...metrics.duur_vs_snelheid,
            series_rel: Array.isArray(metrics.duur_vs_snelheid.series_rel)
              ? metrics.duur_vs_snelheid.series_rel
              : [],
          }
        : { duur_categories: [], snelheid_categories: [], series: [], series_rel: [] };
    const hulpSoorten = Array.isArray(metrics.hulp_soorten) ? metrics.hulp_soorten : [];
    const ervaringPerHulpSoort =
      metrics.ervaring_per_hulp_soort &&
      Array.isArray(metrics.ervaring_per_hulp_soort.series)
        ? metrics.ervaring_per_hulp_soort
        : { categories: [], series: [], series_rel: [] };

    const duurRows = duurCounts.map((item) => [
      item?.label ?? "",
      Number(item?.count) || 0,
      Number(item?.count_rel) || 0,
    ]);
    const snelheidRows = snelheidCounts.map((item) => [
      item?.label ?? "",
      Number(item?.count) || 0,
      Number(item?.count_rel) || 0,
    ]);
    const hulpRows = hulpSoorten.map((item) => [
      item?.label ?? "",
      Number(item?.count) || 0,
      Number(item?.count_rel) || 0,
    ]);

    const heatmapSeries = mode === "rel" ? heatmap.series_rel : heatmap.series;
    const heatmapX = heatmap.snelheid_categories || [];
    const heatmapY = heatmap.duur_categories || [];
    const heatmapHeader = ["Duur \\ Snelheid", ...heatmapX];
    const heatmapRows = heatmapSeries.map((serie, idx) => {
      const label = heatmapY[idx] ?? serie?.name ?? `Serie ${idx + 1}`;
      const values = Array.isArray(serie?.data) ? serie.data : [];
      return [label, ...heatmapX.map((_, xIdx) => Number(values[xIdx]) || 0)];
    });

    const ervaringSeries = mode === "rel" ? ervaringPerHulpSoort.series_rel : ervaringPerHulpSoort.series;
    const ervaringTable = buildStackedRows(ervaringPerHulpSoort.categories, ervaringSeries);

    return [
      {
        title: "Snelheid aanbod (per categorie)",
        header: ["Categorie", "Aantal", "%"],
        rows: snelheidRows,
        suffix,
      },
      {
        title: "Duur jeugdhulp (per categorie)",
        header: ["Categorie", "Aantal", "%"],
        rows: duurRows,
        suffix,
      },
      {
        title: "Duur jeugdhulp × Snelheid aanbod",
        header: heatmapHeader,
        rows: heatmapRows,
        suffix,
      },
      {
        title: "Soorten hulp (totaal)",
        header: ["Soort hulp", "Aantal", "%"],
        rows: hulpRows,
        suffix,
      },
      {
        title: "Soorten per doelgroep",
        header: ervaringTable.header,
        rows: ervaringTable.rows,
        suffix,
      },
    ];
  }

  return [];
};

const buildFilterQuery = (filters, period, mode) => {
  const params = new URLSearchParams();

  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.filter(Boolean).forEach((item) => params.append(key, item));
        return;
      }
      if (value) {
        params.append(key, value);
      }
    });
  }

  if (period && period !== "all") {
    params.set("period", period);
  }

  if (mode) {
    params.set("mode", mode);
  }

  return params.toString();
};

const formatFilterValue = (value) => {
  if (Array.isArray(value)) {
    return value
      .filter(Boolean)
      .map((item) => String(item).replace(/_/g, " ").trim())
      .filter(Boolean)
      .join(", ");
  }
  if (!value) return "";
  return String(value).replace(/_/g, " ").trim();
};

function DashboardPage() {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [period, setPeriod] = useState("all");
  const [mode, setMode] = useState("abs");
  const [filterOptions, setFilterOptions] = useState(DEFAULT_FILTER_OPTIONS);
  const [dashboardData, setDashboardData] = useState(DEFAULT_DASHBOARD_DATA);
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(false);
  const { tokens, user, isVerifying, authorizedFetch } = useAuth();
  const canSeeCeoText = user?.role === "admin" || user?.role === "superadmin";
  const pdfExport = usePdfExport();
  const exportPdf = pdfExport?.exportPdf;
  useEffect(() => {
    if (!canSeeCeoText) {
      setDashboardData((previous) => previous.ceoTextAnswers
        ? { ...previous, ceoTextAnswers: null }
        : previous);
    }
  }, [canSeeCeoText]);
  const dashboardRequests = useMemo(
    () => [
      {
        key: "algemeenMetrics",
        path: "/api/algemeen_metrics/",
        filtered: true,
        apply: (payload = {}) =>
          setDashboardData((prev) => ({
            ...prev,
            algemeenMetrics: payload,
          })),
      },
      {
        key: "contextErvaringMetrics",
        path: "/api/context_ervaring_metrics/",
        filtered: true,
        apply: (payload = {}) =>
          setDashboardData((prev) => ({
            ...prev,
            contextErvaringMetrics: payload,
          })),
      },
      ...(canSeeCeoText ? [{
        key: "ceoTextAnswers",
        path: "/api/ceo_text_answers/",
        filtered: true,
        apply: (payload = {}) =>
          setDashboardData((prev) => ({
            ...prev,
            ceoTextAnswers: payload,
          })),
      }] : []),
      {
        key: "ervaringMetrics",
        path: "/api/ervaring_metrics/",
        filtered: true,
        apply: (payload) =>
          setDashboardData((prev) => ({
            ...prev,
            ervaringMetrics: payload,
          })),
      },
      {
        key: "jeugdhulpMetrics",
        path: "/api/jeugdhulp_metrics/",
        filtered: true,
        apply: (payload) =>
          setDashboardData((prev) => ({
            ...prev,
            jeugdhulpMetrics: payload,
          })),
      },
      {
        key: "jeugdhulpDuurCounts",
        path: "/api/jeugdhulp_duur_counts/",
        filtered: true,
        apply: (payload) =>
          setDashboardData((prev) => ({
            ...prev,
            jeugdhulpDuurCounts: payload,
          })),
      },
      {
        key: "jeugdhulpSnelheidCounts",
        path: "/api/jeugdhulp_snelheid_counts/",
        filtered: true,
        apply: (payload) =>
          setDashboardData((prev) => ({
            ...prev,
            jeugdhulpSnelheidCounts: payload,
          })),
      },
    ],
    [canSeeCeoText]
  );

  useEffect(() => {
    if (isVerifying || !tokens?.access) {
      return undefined;
    }

    const controller = new AbortController();
    let isActive = true;
    const filterQuery = buildFilterQuery(filters, period, mode);

    const withFilters = (path) => {
      if (!filterQuery) {
        return path;
      }
      const separator = path.includes("?") ? "&" : "?";
      return `${path}${separator}${filterQuery}`;
    };

    const fetchJson = async (url) => {
      const response = await authorizedFetch(url, {
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Request failed (${response.status}) for ${url}`);
      }

      return response.json();
    };

    setIsLoadingDashboard(true);

    Promise.all(
      dashboardRequests.map((request) =>
        fetchJson(request.filtered ? withFilters(request.path) : request.path)
      )
    )
      .then((payloads) => {
        if (!isActive) {
          return;
        }
        payloads.forEach((payload, index) => {
          const request = dashboardRequests[index];
          request.apply(payload);
        });
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          console.error("Dashboard fetch failed:", err);
        }
      })
      .finally(() => {
        if (isActive) {
          setIsLoadingDashboard(false);
        }
      });

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [tokens?.access, isVerifying, filters, period, mode, authorizedFetch, dashboardRequests]);

  useEffect(() => {
    if (isVerifying || !tokens?.access) {
      return undefined;
    }

    const controller = new AbortController();

    const fetchOptions = async () => {
      try {
        const response = await authorizedFetch("/api/filters/", {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Request failed (${response.status}) for /api/filters/`);
        }

        const payload = await response.json();
        setFilterOptions((prev) => ({
          ...prev,
          ...payload,
        }));
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Filter options fetch failed:", err);
        }
      }
    };

    fetchOptions();

    return () => controller.abort();
  }, [tokens?.access, isVerifying, authorizedFetch]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => {
      const prevValue = prev[name];
      if (Array.isArray(prevValue) && Array.isArray(value)) {
        if (
          prevValue.length === value.length &&
          prevValue.every((item, index) => item === value[index])
        ) {
          return prev;
        }
      } else if (prevValue === value) {
        return prev;
      }
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const tabs = useMemo(
    () => [
      { key: "algemeen", label: "Algemeen", Component: AlgemeenTab },
      { key: "context-ervaring", label: "Context Ervaring", Component: ContextErvaringTab },
      { key: "ervaring", label: "Ervaring", Component: ErvaringTab },
      { key: "jeugdhulp", label: "Jeugdhulp", Component: JeugdhulpTab },
    ],
    []
  );

  const tabProps = useMemo(
    () => ({
      algemeen: { data: dashboardData, mode },
      "context-ervaring": { data: dashboardData, mode, showTextAnswers: canSeeCeoText },
      ervaring: { data: dashboardData, showTextAnswers: canSeeCeoText },
      jeugdhulp: { data: dashboardData, mode, showTextAnswers: canSeeCeoText },
    }),
    [dashboardData, mode, canSeeCeoText]
  );

  const [activeTab, setActiveTab] = useState(() => getStoredActiveTab() || tabs[0]?.key || "");

  useEffect(() => {
    if (!activeTab || typeof window === "undefined") {
      return;
    }
    window.localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (!tabs.length) {
      return;
    }

    const exists = tabs.some((tab) => tab.key === activeTab);
    if (!exists) {
      setActiveTab(tabs[0].key);
    }
  }, [tabs, activeTab]);

  const activeTabLabel = useMemo(
    () => tabs.find((tab) => tab.key === activeTab)?.label ?? "Dashboard",
    [tabs, activeTab]
  );

  const buildPdfMetaLines = useCallback(() => {
    const lines = [`Tab: ${activeTabLabel}`];
    const periodLabel = PERIOD_LABELS[period] || period;

    if (periodLabel) {
      lines.push(`Periode: ${periodLabel}`);
    }
    lines.push(`Weergave: ${mode === "rel" ? "Relatief" : "Absoluut"}`);

    const filterLines = [];
    FILTER_FIELDS.forEach(({ key, label }) => {
      const value = filters[key];
      if (value) {
        filterLines.push(`${label}: ${formatFilterValue(value)}`);
      }
    });

    if (filterLines.length > 0) {
      lines.push(...filterLines);
    } else {
      lines.push("Filters: geen filters toegepast");
    }

    return lines;
  }, [activeTabLabel, filters, period, mode]);

  const handleDownloadPdf = useCallback(() => {
    if (!exportPdf) {
      return;
    }

    exportPdf({
      title: `Dashboard - ${activeTabLabel}`,
      metaLines: buildPdfMetaLines(),
    });
  }, [exportPdf, activeTabLabel, buildPdfMetaLines]);

  const handleExportData = useCallback(() => {
    const exports = buildExportDefinitions({
      tabKey: activeTab,
      data: dashboardData,
      mode,
    });
    exports.forEach((definition) => downloadCsv(definition));
  }, [activeTab, dashboardData, mode]);

  return (
    <div>
      <Navbar />
      <div className="container">
        <Breadcrumb icon="home-line.png" items={[]} current="Dashboard" />

        <PageHeader
          title="Dashboard"
          status={isLoadingDashboard ? "Data ophalen..." : null}
          actions={
            <>
              <Button
                variant="secondary"
                icon="/icons/download-grijs.png"
                iconPosition="left"
                onClick={handleDownloadPdf}
                disabled={!exportPdf}
              >
                Download PDF
              </Button>
              <Button
                variant="primary"
                icon="/icons/download-paars.png"
                iconPosition="left"
                onClick={handleExportData}
              >
                Export Data
              </Button>
            </>
          }
        />

        <FilterBar
          filtersConfig={FILTER_BAR_FIELDS}
          filters={filters}
          period={period}
          mode={mode}
          onChange={handleFilterChange}
          onPeriodChange={setPeriod}
          onModeChange={setMode}
          options={filterOptions}
        />

        <Suspense fallback={<div>Loading…</div>}>
          <Tabs
            tabs={tabs}
            activeKey={activeTab}
            defaultActiveKey={tabs[0]?.key}
            onTabChange={setActiveTab}
            tabProps={tabProps}
          />
        </Suspense>

        {/* <DashboardCharts
          ratings={ratings}
          counts={counts}
          perYear={perYear}
          summaries={summaries}
          ervaringCounts={ervaringCounts}
          gevoelensCounts={gevoelensCounts}
          agePyramid={agePyramid}
          ervaringOver={ervaringOver}
          teamCategoryCounts={teamCategoryCounts}
        /> */}
      </div>
    </div>
  );
}

export default function Home() {
  return (
    <PdfExportProvider documentTitle="Dashboard">
      <DashboardPage />
    </PdfExportProvider>
  );
}
